import PaneSwitchIcon from "app/components/Icons/PaneSwitchIcon";
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import {
  selectIsLoading,
  selectPlanningItems,
} from "../redux/selector";
import { backlogActions } from "../redux/slice";
import { PlanningViewBy } from "../types";
import AddButton from "./AddButton";
import Filter from "./Filter";
import PlanningModals from "./PlanningModals";
import RenderTiles from "./RenderTiles";
import SprintTiles from "./SprintTiles";
import PlanningGroupTile from "./Tiles";
import TilesSkeleton from "./TilesSkeleton";
import { usePlanningTileFilter } from "../hooks/usePlanningTileFilter";

export default function Planning({ onClose }: { onClose: () => void }) {
  const { boardId ,project} = useParams();
  const dispatch = useDispatch();

  const [activeModal, setActiveModal] = useState<PlanningViewBy | null>(null);

  const { backlog, selectedView } = useSelector(selectPlanningItems);
  const isLoading = useSelector(selectIsLoading);
  const {
    handleTileClick,
    isTileActive,
    getTileClickHandler,
    getTileActiveState,
  } = usePlanningTileFilter();

  useEffect(() => {
    if (!boardId) return;

    dispatch(
      backlogActions.fetchBacklogsPlanning({
        boardId: boardId as string,
        viewBy: selectedView.viewBy ?? PlanningViewBy.COMPONENTS,
      }),
    );
  }, [boardId, selectedView.viewBy, dispatch]);

  const skeletons = useMemo(
    () => Array.from({ length: 3 }, (_, i) => <TilesSkeleton key={i} />),
    [],
  );

  const handleAddClick = (type: PlanningViewBy) => {
    setActiveModal(type);
  };
  const onTypeChange = (type: PlanningViewBy) => {
    dispatch(backlogActions.setSelectedPlanningTile(null));
    dispatch(
      backlogActions.setPlanningItems({
        backlog: backlog,
        selectedView: { groups: selectedView.groups, viewBy: type },
      }),
    );
  };
  const onCloseModal = () => {
    setActiveModal(null);
    dispatch(
      backlogActions.fetchBacklogsPlanning({
        boardId: boardId as string,
        viewBy: selectedView.viewBy,
        skipLoading: true,
      }),
    );
  };
  return (
    <div className="w-[320px] min-w-0 flex-shrink-0 pl-[16px] border-l border-secondary flex flex-col h-[calc(100vh-160px)] overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between mb-[24px]">
        <p className="text-lg font-medium">Planning</p>
        <button className="size-[20px]" onClick={onClose}>
          <PaneSwitchIcon color="#253858" />
        </button>
      </div>

      {/* Content */}
      <div className="flex-grow overflow-y-auto overflow-x-hidden min-w-0">
        <div className="flex flex-col gap-[10px] min-w-0">
          <PlanningGroupTile
            isActive={isTileActive("backlog")}
            group={{
              name: "Backlog",
              ticketTypes: backlog.ticketTypes,
              ticketIds: backlog.ticketIds,
            }}
            viewBy={PlanningViewBy.BACKLOG}
            onTileClick={() =>
              handleTileClick("backlog", backlog.ticketIds ?? [])
            }
          />

          <Filter
            selectedViewType={selectedView.viewBy as PlanningViewBy}
            onChange={onTypeChange}
          />

          {isLoading && skeletons}

          {!isLoading && selectedView && (
            <div className="flex flex-col gap-[10px]">
              {selectedView.viewBy === PlanningViewBy.SPRINTS ? (
                <SprintTiles
                  groups={selectedView.groups}
                  viewBy={selectedView.viewBy}
                  getTileActiveState={(group) =>
                    getTileActiveState(selectedView.viewBy, group)
                  }
                  getTileClickHandler={(group) =>
                    getTileClickHandler(selectedView.viewBy, group)
                  }
                  onAddClick={() => handleAddClick(selectedView.viewBy)}
                />
              ) : (
                selectedView.groups.map((group) => (
                  <RenderTiles
                    key={group.id ?? group.name}
                    group={group}
                    viewBy={selectedView.viewBy}
                    isActive={getTileActiveState(selectedView.viewBy, group)}
                    onTileClick={getTileClickHandler(
                      selectedView.viewBy,
                      group,
                    )}
                  />
                ))
              )}
              {selectedView.viewBy !== PlanningViewBy.SPRINTS && (
                <AddButton
                  title={selectedView.viewBy}
                  onClick={() => handleAddClick(selectedView.viewBy)}
                />
              )}
            </div>
          )}
        </div>
      </div>

      {/* ✅ Clean modal usage */}
      <PlanningModals activeModal={activeModal} onClose={onCloseModal} projectId={project as string}   />
    </div>
  );
}
